import { useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  helpdeskDashboardService,
  type HelpdeskDashboardStats,
} from '@/lib/services/helpdeskDashboardService'
import {
  taskSlaService,
  type TaskSlaDashboardData,
} from '@/lib/services/taskSlaService'
import { useAuthStore } from '@/store/auth'

const STALE_TIME_MS = 60 * 1000

const defaultTaskSla: TaskSlaDashboardData = {
  sla: {
    total_tasks: 0,
    breached: 0,
    within_sla: 0,
    response_met_rate: 0,
    resolution_met_rate: 0,
    avg_resolution_minutes: 0,
  },
  aging: {
    fresh: 0,
    aging: 0,
    old: 0,
    critical: 0,
    total_open: 0,
  },
}

export const defaultDashboardStats: HelpdeskDashboardStats = {
  unresolved: 0,
  overdue: 0,
  due_today: 0,
  open: 0,
  on_hold: 0,
  unassigned: 0,
  resolved: 0,
  received: 0,
  average_first_response_seconds: 0,
  resolution_within_sla_percent: 0,
  trends_today: [],
  trends_yesterday: [],
  kpi_open: 0,
  kpi_on_hold_parts: 0,
  kpi_on_hold_parts_avg_hold: null,
  kpi_on_hold_parts_max_hold: null,
  kpi_on_hold_customer: 0,
  kpi_on_hold_customer_avg_hold: null,
  kpi_on_hold_customer_max_hold: null,
  kpi_resolved_incomplete: 0,
  kpi_closed: 0,
  kpi_cancelled: 0,
  ticket_source: { period: 'last_7_days', total_active: 0, sources: [] },
  agent_performance: [],
}

function mergeTicketStats(data: HelpdeskDashboardStats): HelpdeskDashboardStats {
  return {
    ...defaultDashboardStats,
    ...data,
    trends_today: data.trends_today ?? [],
    trends_yesterday: data.trends_yesterday ?? [],
    ticket_source: data.ticket_source ?? defaultDashboardStats.ticket_source,
    agent_performance: data.agent_performance ?? [],
  }
}

export function useDashboardData() {
  const clientKey = useAuthStore((state) => {
    const user = state.user as ({ id?: number; default_client_code?: string } | null)
    return user?.default_client_code ?? user?.id ?? 'anon'
  })

  const ticketQuery = useQuery({
    queryKey: ['helpdesk-dashboard-stats', clientKey],
    queryFn: () => helpdeskDashboardService.getStats(),
    staleTime: STALE_TIME_MS,
  })

  const taskQuery = useQuery({
    queryKey: ['dashboard-task-sla', clientKey],
    queryFn: () => taskSlaService.getTaskSla(),
    staleTime: STALE_TIME_MS,
  })

  useEffect(() => {
    if (ticketQuery.isError) {
      console.warn('Helpdesk ticket stats not available:', ticketQuery.error)
    }
  }, [ticketQuery.isError, ticketQuery.error])

  useEffect(() => {
    if (taskQuery.isError) {
      console.warn('Task SLA metrics not available:', taskQuery.error)
    }
  }, [taskQuery.isError, taskQuery.error])

  const refreshedAtMs = Math.max(ticketQuery.dataUpdatedAt, taskQuery.dataUpdatedAt)

  return {
    ticketStats: ticketQuery.data ? mergeTicketStats(ticketQuery.data) : defaultDashboardStats,
    taskSla: taskQuery.data ?? defaultTaskSla,
    ticketLoading: ticketQuery.isLoading,
    taskSlaLoading: taskQuery.isLoading,
    refreshedAt: refreshedAtMs > 0 ? new Date(refreshedAtMs) : null,
  }
}
